import _get from 'lodash/get'
import getDecoratedTitle from '../../plugins/filters/getDecoratedTitle/getDecoratedTitle.filter'
import RouteUtils from '~/route.utils'

/**
 * Redirect to URL
 * @param redirect
 * @param item
 * @param shouldRedirect
 * @param pathRedirect
 * @param isNotFound
 * @returns {Promise<never>|any}
 */
function redirectUrl (redirect, { item, shouldRedirect, pathRedirect, isNotFound, isGone }) {
  if (isGone) {
    return Promise.reject({ statusCode: 410 })
  }
  if (isNotFound) {
    return Promise.reject({ statusCode: 404 })
  }
  return shouldRedirect ? redirect(301, pathRedirect) : Promise.resolve(item)
}

/**
 * Condition to redirect to episode
 * @param route
 * @param video
 * @returns {boolean}
 */
function shouldRedirectToEpisode (route, video) {
  const seasonTitle = getDecoratedTitle(_get(video, 'seasonInfo.title'))
  const episodeTitle = getDecoratedTitle(_get(video, 'title'))

  return (
    !route.params.seasonId ||
    route.params.seasonId !== _get(video, 'seasonInfo.id') ||
    !route.params.seasonTitle ||
    route.params.seasonTitle !== seasonTitle ||
    !route.params.episodeTitle ||
    route.params.episodeTitle !== episodeTitle
  )
}

/**
 * Condition to redirect to video
 * @param route
 * @param video
 * @returns {boolean}
 */
function shouldRedirectToVideo (route, video) {
  const title = getDecoratedTitle(_get(video, 'title'))
  return (
    !route.params.itemTitle ||
    route.params.itemTitle !== title
  )
}

/**
 * Condition to redirect to category
 * @param route
 * @param category
 * @returns {boolean}
 */
function shouldRedirectToCategory (route, category) {
  const title = getDecoratedTitle(_get(category, 'title'))
  return (
    !route.params.itemTitle ||
    route.params.itemTitle !== title
  )
}

/**
 * Condition to redirect to season
 * @param route
 * @param season
 * @returns {boolean}
 */
function shouldRedirectToSeason (route, season) {
  const title = getDecoratedTitle(_get(season, 'title'))
  return (
    !route.params.itemTitle ||
    route.params.itemTitle !== title
  )
}

/**
 * Condition to redirect to package
 * @param route
 * @param pack
 * @returns {boolean}
 */
function shouldRedirectToPackage (route, pack) {
  const title = getDecoratedTitle(_get(pack, 'title'))
  return (
    !route.params.itemTitle ||
    route.params.itemTitle !== title
  )
}

// -----------------------------------------------------

export default {
  /**
   * Parse URL Category
   * @param store
   * @param route
   * @param redirect
   * @returns {Promise<never> | Promise<void>}
   */
  parseUrlCategory (store, route, redirect) {
    const category = store.getters['categories/getSelectedCategory']

    return redirectUrl(redirect, {
      isNotFound: !category.id,
      shouldRedirect: shouldRedirectToCategory(route, category),
      pathRedirect: {
        name: 'category',
        params: {
          id: _get(route, 'params.id', null),
          itemTitle: getDecoratedTitle(_get(category, 'title'))
        }
      }
    })
  },
  /**
   * Parse URL for video
   * @param video
   * @param route
   * @param redirect
   * @returns {Promise<never>|*}
   */
  parseUrlItemVideo (video, route, redirect) {
    return redirectUrl(redirect, {
      item: video,
      isNotFound: !video.id,
      isGone: video.error?.status === 410,
      shouldRedirect: shouldRedirectToVideo(route, video),
      pathRedirect: {
        name: RouteUtils.ROUTE.FIP.MOVIE,
        params: {
          id: _get(route, 'params.id', null),
          itemTitle: getDecoratedTitle(_get(video, 'title'))
        }
      }
    })
  },

  /**
   * Parse URL episode - Redirect to movie if type is movie
   * @param episode
   * @param route
   * @param redirect
   * @returns {Promise<never>|(Promise<never> | Promise<void>)}
   */
  parseUrlEpisode (episode, route, redirect) {
    return redirectUrl(redirect, {
      item: episode,
      isNotFound: !episode.id,
      shouldRedirect: shouldRedirectToEpisode(route, episode),
      pathRedirect: {
        name: RouteUtils.ROUTE.FIP.EPISODE,
        params: {
          id: _get(route, 'params.id', null),
          seasonId: _get(episode, 'seasonInfo.id'),
          seasonTitle: getDecoratedTitle(_get(episode, 'seasonInfo.title')),
          episodeTitle: getDecoratedTitle(_get(episode, 'title'))
        }
      }
    })
  },
  /**
   * Parse URL Season
   * @param season
   * @param route
   * @param redirect
   * @returns {Promise<never> | Promise<void>}
   */
  parseUrlSeason (season, route, redirect) {
    return redirectUrl(redirect, {
      item: season,
      isNotFound: !season.id,
      shouldRedirect: shouldRedirectToSeason(route, season),
      pathRedirect: {
        name: RouteUtils.ROUTE.FIP.SEASON,
        params: {
          id: _get(route, 'params.id', null),
          itemTitle: getDecoratedTitle(_get(season, 'title'))
        }
      }
    })
  },
  /**
   * Parse URL Package
   * @param pack
   * @param route
   * @param redirect
   * @returns {Promise<never> | Promise<void>}
   */
  parseUrlPackage (pack, route, redirect) {
    return redirectUrl(redirect, {
      item: pack,
      isNotFound: !pack.id,
      shouldRedirect: shouldRedirectToPackage(route, pack),
      pathRedirect: {
        name: RouteUtils.ROUTE.FIP.PACKAGE,
        params: {
          id: _get(route, 'params.id', null),
          itemTitle: getDecoratedTitle(_get(pack, 'title'))
        }
      }
    })
  }
}
